import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as ArrowDownIcon } from "../../../Assets/svg/arrow-down.svg";
import { ReactComponent as ArrowUpIcon } from "../../../Assets/svg/arrow-up.svg";
import { apiCall, apiCallGeneric } from "../../../services/api-service.js";
import personIcon from "../../../Assets/svg/person_ic.svg";

const getApiEndpoint = (url) => {
  const baseUrl = process.env.REACT_APP_API_URL || "";
  const endpoint = (url || "").split("?")[0];
  return endpoint.startsWith(baseUrl)
    ? endpoint.slice(baseUrl.length).replace(/^\/+/, "")
    : endpoint.replace(/^\/+/, "");
};

const CustomDropdownWithSearch = ({
  headerPrefix,
  showImage = false,
  searchId = -1,
  onChangeIndex = {},
  onDropdownChange,
  apiUrl,
  placeholder,
  selectedValue,
  selectedOption,
  selectedOptions = [],
  params = {},
  closeOnSelect = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  /*  const [selectedOption, setSelectedOption] = useState(null); */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const [openDirection, setOpenDirection] = useState("down");

  // Mock API call to fetch options
  const fetchOptions = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!apiUrl) return;

      const isAbsolute = /^https?:\/\//i.test(apiUrl);
      let response;
      if (isAbsolute) {
        response = await apiCallGeneric("get", apiUrl, null, {
          params: {
            ...(params ?? {}),
            term: searchTerm,
          },
        });
      } else {
        response = await apiCall("get", getApiEndpoint(apiUrl), null, {
          params: {
            ...(params ?? {}),
            term: searchTerm,
          },
        });
      }

      const res = response?.data;

      if (res?.success) {
        const list = res.suggestionlist || res.suggestion_list || res.data || [];
        setOptions(list);
        setFilteredOptions(list);
      } else {
        setOptions([]);
        setFilteredOptions([]);
      }
    } catch (err) {
      console.error("Error fetching options:", err);
      setError("No options found.");
      setOptions([]);
      setFilteredOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const serializedParams = JSON.stringify(params);

  // Fetch options when component mounts, search term changes, apiUrl changes, or params change
  useEffect(() => {
    fetchOptions();
  }, [searchTerm, apiUrl, serializedParams]);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const lower = searchTerm.toLowerCase();
      setFilteredOptions(options.filter((option) => (option?.label ?? "").toLowerCase().includes(lower)));
    }
  }, [searchTerm, options]);

  // Handle outside click to close the dropdown
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 220; // max-height (200px) + search box & padding
      if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
        setOpenDirection("up");
      } else {
        setOpenDirection("down");
      }
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        fetchOptions();
      }
      return next;
    });
  };

  const selectOption = (option) => {
    /* setSelectedOption(option); */
    if (Object.keys(onChangeIndex).length === 0) {
      onDropdownChange(option);
    } else {
      onDropdownChange(option, onChangeIndex);
    }

    setSearchTerm("");
    if (closeOnSelect) {
      setIsOpen(false);
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative", minHeight: "40px", zIndex: isOpen ? 1000 : 1 }}>
      {/* Dropdown Header */}
      <div
        onClick={toggleDropdown}
        style={{
          minHeight: "40px",
          height: "40px",
          padding: "8px 12px",
          border: isOpen ? "1px solid #6929E2" : "1px solid #E5E5E5",
          borderRadius: "8px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#FFFFFF",
          color: selectedOption || selectedValue !== "" ? "#1E1E1E" : "#81878C",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden", flexWrap: "nowrap", flex: 1, minWidth: 0 }}>
          {headerPrefix}
          {showImage && (
            <img src={selectedOption?.profile || personIcon} alt="" style={{ width: "22px", height: "22px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          )}
          <span style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", minWidth: 0, flex: 1, fontSize: "14px" }}>
            {selectedOption ? selectedOption.label : selectedValue != null && selectedValue !== "" ? selectedValue : placeholder}
          </span>
        </div>
        {(selectedOption || (selectedValue != null && selectedValue !== "")) && (
          <span
            style={{ marginLeft: "6px", cursor: "pointer", display: "flex", alignItems: "center", color: "#81878C", fontSize: "14px", fontWeight: "bold", flexShrink: 0 }}
            title="Clear"
            onClick={(e) => {
              e.stopPropagation();
              if (Object.keys(onChangeIndex || {}).length === 0) {
                onDropdownChange(null);
              } else {
                onDropdownChange(null, onChangeIndex);
              }
              setSearchTerm("");
            }}
          >
            &#x2715;
          </span>
        )}
        <span style={{ marginLeft: "8px", display: "flex", alignItems: "center", flexShrink: 0 }}>{isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}</span>
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            ...(openDirection === "up" ? { bottom: "100%", marginBottom: "4px" } : { top: "100%", marginTop: "4px" }),
            left: 0,
            right: 0,
            border: "1px solid #ccc",
            borderRadius: "4px",
            background: "#fff",
            zIndex: 1000,
            maxHeight: "200px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* Search Input */}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            style={{
              width: "100%",
              padding: "8px",
              border: "none",
              borderBottom: "1px solid #ccc",
              boxSizing: "border-box",
              color: "#1E1E1E",
            }}
          />

          {/* Options */}
          {loading ? (
            <div style={{ padding: "10px", textAlign: "center" }}>Loading...</div>
          ) : error ? (
            <div style={{ padding: "10px", textAlign: "center", color: "red" }}>{error}</div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                onClick={() => selectOption(option)}
                style={{
                  padding: "10px",
                  cursor: "pointer",
                  background: (selectedOption && (
                                (selectedOption.id !== undefined && selectedOption.id !== null && selectedOption.id === option.id) ||
                                (selectedOption.value !== undefined && selectedOption.value !== null && selectedOption.value === option.value)
                              )) ||
                              (selectedOptions && selectedOptions.some(item => (
                                (item.id !== undefined && item.id !== null && item.id === option.id) ||
                                (item.value !== undefined && item.value !== null && item.value === option.value)
                              )))
                              ? "#e6f0ff" : "#fff",
                  color: "#1E1E1E",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  overflow: "hidden"
                }}
              >
                {showImage && (
                  <img src={option.profile || personIcon} alt="" style={{ width: "24px", height: "24px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                )}
                <span style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", flex: 1 }}>
                  {option.label}
                </span>
              </div>
            ))
          ) : (
            <div style={{ padding: "10px", textAlign: "center" }}>No records found.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDropdownWithSearch;
